import React from 'react'
import { FormattedMessage } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import withMountGuard from 'hocs/withMountGuard'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { LUX_PLUS_TOURS_CAMPAIGN } from 'luxPlus/constants/base'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import PageBanner from 'LuxKit/Banners/PageBanner'
import Colour from 'components/utils/Colour'
import { getThemeClassName } from 'lib/theme/themeUtils'
import LuxPlusMonthTakeoverBackground from 'takeovers/luxPlusMonthTakeover/components/LuxPlusMonthTakeoverBackground'
import styled from 'styled-components'
import TextLink from 'LuxKit/TextLink'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useLuxPlusToursOfferBanner from 'luxPlus/hooks/useLuxPlusToursOfferBanner'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'

const BannerWrapper = styled.div`
  position: relative;
`

const Background = styled(LuxPlusMonthTakeoverBackground)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`

const Content = styled.div`
  position: relative;
`

function LuxPlusToursOfferBanner() {
  const {
    showBanner,
    onDismiss,
    ctaLabel,
    ctaTo,
    ctaHref,
    ctaOnClick,
  } = useLuxPlusToursOfferBanner()

  if (!showBanner) return null

  return (
    <BannerWrapper>
      <Background />
      <Content className={getThemeClassName({ mobile: 'inverse' })}>
        <CSSBreakpoint min="tablet">
          <PageBanner
            kind="none"
            density="compact"
            align="start"
            tabletAlign="center"
            icon={<LineCompassIcon size="M" />}
            primaryActionButtonProps={{
              children: ctaLabel,
              to: ctaTo,
              href: ctaHref,
              onClick: ctaOnClick,
              size: 'small',
            }}
            onDismiss={onDismiss}
          >
            <FormattedMessage
              id="h-1yF-u8A7W2VYyZXts3"
              defaultMessage="<bold>LuxPlus<plus>+</plus></bold> members save 5% on all Tours when using code <bold>{promoCode}</bold> at checkout. Ends {endDate}. T&Cs apply."
              description="LuxPlus+ tours promotional banner message describing the offer details"
              values={{
                bold: (chunks) => <b>{chunks}</b>,
                plus: (chunks) => <Colour value="highlight-lux-plus">{chunks}</Colour>,
                promoCode: LUX_PLUS_TOURS_CAMPAIGN.PROMO_CODE,
                endDate: <FormatDateTime value={LUX_PLUS_TOURS_CAMPAIGN.END_DATE} day="numeric" month="short" year="numeric" />,
              }}
            />
          </PageBanner>
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <PageBanner
            kind="none"
            density="compact"
            align="start"
            tabletAlign="center"
            onDismiss={onDismiss}
          >
            <BodyText variant="small">
              <FormattedMessage
                id="LVm3ckvAEu_df3Rz_LRk"
                defaultMessage="<bold>LuxPlus<plus>+</plus></bold> members save 5% on all Tours when using code <bold>{promoCode}</bold> at checkout. Ends {endDate}. T&Cs apply. <link>{ctaLabel}</link>"
                description="LuxPlus+ tours promotional banner message with call-to-action link"
                values={{
                  bold: (chunks) => <b>{chunks}</b>,
                  plus: (chunks) => <Colour value="highlight-lux-plus">{chunks}</Colour>,
                  promoCode: LUX_PLUS_TOURS_CAMPAIGN.PROMO_CODE,
                  endDate: <FormatDateTime value={LUX_PLUS_TOURS_CAMPAIGN.END_DATE} day="numeric" month="short" year="numeric" />,
                  ctaLabel,
                  link: (chunks) => <TextLink
                    to={ctaTo}
                    href={ctaHref}
                    onClick={ctaOnClick}
                    size="caption"
                  >{chunks}
                  </TextLink>,
                }}
              />
            </BodyText>
          </PageBanner>
        </CSSBreakpoint>
      </Content>
    </BannerWrapper>
  )
}

export default withMountGuard(() => {
  return useAppSelector(getIsLuxPlusEnabled)
})(LuxPlusToursOfferBanner)
